import {
  testTimeUnknown,
  currentSeries,
  outrankedTrident,
  tridentReadsNone,
} from '../../shared/trident-trust.js';
import { readingsAgree } from '../../shared/instrument-margin.js';
import { daysSinceCalendarDay } from './equipment-catalog.js';

// Compact "how long ago" — the freshness signal on every param card.
// A reefer trusts a 2h-old reading; a 5d-old one means "go test again."
// Sub-minute reads as "just now"; over a year falls back to a date.
export function relativeTimeShort(input) {
  if (input == null) return null;
  const ts = typeof input === 'number' ? input : new Date(input).getTime();
  if (!Number.isFinite(ts)) return null;
  const ms = Math.max(0, Date.now() - ts);
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  if (ms < 30 * 86_400_000) return `${Math.round(ms / 86_400_000)}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Per-param "test cadence" floor — readings older than this read as
// stale in the param-card status line so a reefer doesn't trust a
// 4-day-old alk number sitting at a green band. Hours.
const STALE_AFTER_HOURS = {
  alk: 96,
  calcium: 168,
  magnesium: 168,
  nitrate: 168,
  phosphate: 168,
  ph: 72,
  salinity: 168,
  temp: 48,
};

export function isReadingStale(paramKey, input) {
  if (input == null) return false;
  const ts = typeof input === 'number' ? input : new Date(input).getTime();
  if (!Number.isFinite(ts)) return false;
  const hours = STALE_AFTER_HOURS[paramKey] ?? 168;
  return Date.now() - ts > hours * 3_600_000;
}

// Trust horizons: past these, a reading is no longer the tank's current
// state and the card shows a STALE pill (not OK/HIGH/LOW). SOURCE-aware
// because a probe reporting every 15 minutes and a weekly manual test
// have entirely different "silent for too long" thresholds. Frozen-
// value detection catches the worse case: the Apex keeps publishing the
// last Trident result every 15 min, ts looks fresh, but the value is
// dead. Keep in sync with server/staleness.js (the authority).

const AUTOMATED_METHODS_CLIENT = new Set(['apex', 'hydros']);
export function isAutomatedMethodClient(method) {
  return AUTOMATED_METHODS_CLIENT.has(method);
}

const STALE_TRUST_DAYS_MANUAL = {
  alk: 21,
  calcium: 35,
  magnesium: 35,
  nitrate: 35,
  phosphate: 35,
  salinity: 21,
  temp: 14,
  ph: 14,
};
const STALE_TRUST_DEFAULT_DAYS_MANUAL = 30;
const STALE_SILENT_HOURS_AUTOMATED = 72;
const FROZEN_VALUE_HOURS_AUTOMATED = 72;
// Source-agnostic STUCK detection — six identical readings within 24h
// is the signature of a stuck probe or a calibration-locked test kit.
// Mirror of STUCK_REPEAT_WINDOW_HOURS / STUCK_REPEAT_MIN_COUNT in
// server/staleness.js. Keep in sync.
const STUCK_REPEAT_WINDOW_HOURS = 24;
const STUCK_REPEAT_MIN_COUNT = 6;

function parseTsClient(input) {
  if (input == null) return null;
  const t = typeof input === 'number' ? input : new Date(input).getTime();
  return Number.isFinite(t) ? t : null;
}

// SILENT — no new data for too long. Probe horizon is hours; manual
// horizon is days.
export function isReadingSilent(paramKey, ts, method) {
  const t = parseTsClient(ts);
  if (t == null) return false;
  const hours = isAutomatedMethodClient(method)
    ? STALE_SILENT_HOURS_AUTOMATED
    : (STALE_TRUST_DAYS_MANUAL[paramKey] ?? STALE_TRUST_DEFAULT_DAYS_MANUAL) * 24;
  return Date.now() - t > hours * 3_600_000;
}

// FROZEN — value hasn't changed across enough recent history. Only
// meaningful for automated sources. `readings` is the per-param series
// newest-first (the same array passed to Sparkline / computeTrend).
export function isValueFrozenClient(readings, method) {
  if (!isAutomatedMethodClient(method)) return false;
  if (!Array.isArray(readings) || readings.length < 2) return false;
  const latest = Number(readings[0].value);
  if (!Number.isFinite(latest)) return false;
  let runStartTs = parseTsClient(readings[0].ts);
  for (let i = 1; i < readings.length; i++) {
    const v = Number(readings[i].value);
    if (!Number.isFinite(v)) continue;
    if (v !== latest) {
      // Value changed; "run start" is the most recent same-value reading.
      return Date.now() - runStartTs > FROZEN_VALUE_HOURS_AUTOMATED * 3_600_000;
    }
    const t = parseTsClient(readings[i].ts);
    if (t != null) runStartTs = t;
  }
  if (runStartTs == null) return false;
  return Date.now() - runStartTs > FROZEN_VALUE_HOURS_AUTOMATED * 3_600_000;
}

// STUCK (source-agnostic) — six identical readings within 24h is the
// signature of a stuck probe / calibration-locked kit regardless of
// source. Mirror of isStuckByRepetition in server/staleness.js.
export function isStuckByRepetitionClient(readings) {
  if (!Array.isArray(readings) || readings.length < STUCK_REPEAT_MIN_COUNT) return false;
  const cutoff = Date.now() - STUCK_REPEAT_WINDOW_HOURS * 3_600_000;
  const inWindow = [];
  for (const r of readings) {
    const t = parseTsClient(r?.ts);
    if (t == null) continue;
    if (t < cutoff) continue;
    const v = Number(r.value);
    if (!Number.isFinite(v)) continue;
    inWindow.push(v);
  }
  if (inWindow.length < STUCK_REPEAT_MIN_COUNT) return false;
  const first = inWindow[0];
  for (const v of inWindow) if (v !== first) return false;
  return true;
}

// UNCHANGED (source-agnostic, SOFT) — a value that has not moved across
// several readings spanning months. Mirror of isUnchangedTooLong in
// server/staleness.js; see there for why this is separate from the three
// hard checks and why it never voids a reading.
export const UNCHANGED_MIN_COUNT = 3;
export const UNCHANGED_MIN_SPAN_DAYS = 45;

export function isUnchangedTooLongClient(readings) {
  if (!Array.isArray(readings) || readings.length < UNCHANGED_MIN_COUNT) return false;
  const latest = Number(readings[0]?.value);
  if (!Number.isFinite(latest)) return false;
  let run = 1;
  let runStartTs = parseTsClient(readings[0].ts);
  for (let i = 1; i < readings.length; i++) {
    const v = Number(readings[i].value);
    if (!Number.isFinite(v)) continue;
    if (v !== latest) break;
    run++;
    const t = parseTsClient(readings[i].ts);
    if (t != null) runStartTs = t;
  }
  if (run < UNCHANGED_MIN_COUNT || runStartTs == null) return false;
  return Date.now() - runStartTs >= UNCHANGED_MIN_SPAN_DAYS * 86_400_000;
}

// One param's rows, newest first, as the server reads the tank's current
// state (server/staleness.js currentReadings): the keeper's own recent test
// before Trident rows with no test time.
const ownTestCounts = (paramKey) => (r) => !isReadingSilent(paramKey, r.ts, r.method || null);

export function currentReadingsClient(readings, paramKey) {
  return currentSeries(readings, paramKey, ownTestCounts(paramKey));
}

// A Trident number that disagrees with the keeper's own counting test, or
// null (shared/trident-trust.js outrankedTrident).
export function outrankedTridentClient(readings, paramKey) {
  return outrankedTrident(readings, paramKey, readingsAgree, ownTestCounts(paramKey));
}

// One call, used by the param card. Returns {stale, reason} or null.
// `suspect` is the soft tier: the card keeps showing the value and the
// score keeps counting it, but the keeper is asked to confirm it is
// current. Callers gating on `.stale` see no change.
export function assessFreshnessClient(readings, paramKey) {
  const latest = Array.isArray(readings) ? readings[0] : null;
  if (!latest) return null;
  const method = latest.method || null;
  if (isReadingSilent(paramKey, latest.ts, method)) {
    return { stale: true, reason: 'silent' };
  }
  if (!testTimeUnknown(latest, paramKey) && isStuckByRepetitionClient(readings)) {
    return { stale: true, reason: 'stuck' };
  }
  if (!tridentReadsNone(latest, paramKey) && isValueFrozenClient(readings, method)) {
    return { stale: true, reason: 'frozen' };
  }
  // The server's rule (shared/trident-trust.js): a Trident result has no
  // test time, so it is never shown as current. After silent / stuck /
  // frozen, as on the server.
  if (testTimeUnknown(latest, paramKey)) {
    return { stale: true, reason: 'test_time_unknown' };
  }
  if (isUnchangedTooLongClient(readings)) {
    return { stale: false, reason: null, suspect: true, suspect_reason: 'unchanged' };
  }
  return { stale: false, reason: null };
}

// Legacy single-signal helper kept for the few callers that only have a
// timestamp + method. New code should use assessFreshnessClient.
export function isReadingUntrusted(paramKey, input, method = null) {
  return isReadingSilent(paramKey, input, method);
}

export function daysSinceReading(input) {
  const t = parseTsClient(input);
  if (t == null) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const sameDay = d.toDateString() === new Date().toDateString();
  if (sameDay) return 'today';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Calendar days in the keeper's own time zone. Rounding hours put a task due
// 8 am tomorrow under "Due today" at 9 pm, left one due 7 pm today out of
// Today at 6 am, and read 11 hours overdue as "Due today".
export function daysFromNow(iso, now = new Date()) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((day(d) - day(now)) / 86400000) || 0;
}

// Group readings by param, already newest-first from the API.
export function groupReadings(readings) {
  const byParam = {};
  for (const r of readings || []) {
    if (!byParam[r.param]) byParam[r.param] = [];
    byParam[r.param].push(r);
  }
  return byParam;
}

// Supply forecast math — mirrors the server/demo contract.
export function supplyForecast(s) {
  if (!s) return { status: 'unknown', label: '—' };
  // A supply saved with no amount left is not out: Number(null) is 0, and
  // Today read "Reorder · Out" for good. The server's count leaves it out too.
  if (s.remaining == null || s.remaining === '') return { status: 'unknown', label: '—' };
  const remaining = Number(s.remaining) || 0;
  const dailyUse = Number(s.daily_use) || 0;
  const size = Number(s.size) || 0;
  if (remaining <= 0) return { status: 'critical', label: 'Out', daysLow: 0, daysHigh: 0 };
  // Event-consumed supplies (salt drawn down by water changes) have no
  // daily_use — judge them by fraction of the package left instead of
  // days. Without this they read 'plenty' until the moment they hit 0
  // (review finding). Mirrors getLowSupplyCountsForTanks in server/db.js.
  if (dailyUse <= 0 && size > 0) {
    const pct = remaining / size;
    if (pct <= 0.1) return { status: 'critical', label: `${Math.round(pct * 100)}% left` };
    if (pct <= 0.25) return { status: 'low', label: `${Math.round(pct * 100)}% left` };
    return { status: 'adequate', label: `${Math.round(pct * 100)}% left` };
  }
  if (dailyUse <= 0) return { status: 'adequate', label: 'plenty', daysLow: 60, daysHigh: 99 };
  const days = remaining / dailyUse;
  const daysLow = Math.max(1, Math.floor(days * 0.85));
  const daysHigh = Math.ceil(days * 1.15);
  const reorderAt = s.reorder_at || 14;
  const status = days <= 3 ? 'critical' : days <= reorderAt ? 'low' : 'adequate';
  const label = status === 'critical' ? `~${daysLow} d left` : `~${daysLow}–${daysHigh} d`;
  return { status, label, daysLow, daysHigh, days };
}

const CARTRIDGE_LABELS = {
  rodi_sediment: 'RO/DI sediment',
  rodi_carbon: 'RO/DI carbon',
  rodi_membrane: 'RO/DI membrane',
  rodi_di: 'RO/DI DI resin',
};

// Synthesise supply-runway entries from RO/DI cartridge equipment rows
// so Home's "running low" list + SuppliesView pick them up alongside
// salt, foods, supplements. installed_at + replace_every_days drive
// the remaining-days math; status thresholds match supplyForecast.
//
// Output shape mirrors a `supplies` row so consumers don't need to
// branch — pseudoId stays unique by prefixing the equipment id.
export function equipmentSupplyItems(tank) {
  const items = [];
  for (const e of tank?.equipment || []) {
    const label = CARTRIDGE_LABELS[e.kind];
    if (!label) continue;
    if (!e.installed_at || !e.replace_every_days) continue;
    // Calendar days, as Equipment counts them: one part, one due date.
    const elapsed = daysSinceCalendarDay(e.installed_at);
    if (elapsed == null) continue;
    const remainingDays = e.replace_every_days - Math.max(0, elapsed);
    const f = runwayBucket(remainingDays);
    items.push({
      id: `equipment:${e.id}`,
      name: `${label}${e.brand ? ' (' + e.brand + ')' : ''}`,
      kind: 'cartridge',
      remaining: remainingDays,
      daily_use: 1, // formal supplyForecast would call this 1 unit per day
      equipment_id: e.id,
      f,
    });
  }
  return items;
}

// Synthesise supply-runway entries from equipment_parts rows so the
// "running low" surface picks up UV bulbs, probes, impellers, etc.
// alongside salt and reagents. installed_at + replace_every_days drive
// the runway; status thresholds mirror equipmentSupplyItems above so a
// part past due shows next to a salt bucket that's out.
//
// The output shape mimics a supplies row — caller code is
// branchless. `reorder_url` rides on the item so Today's Plan can
// surface a one-tap reorder link when present.
export function equipmentPartItems(tank, catalog = null) {
  const items = [];
  for (const e of tank?.equipment || []) {
    const parts = Array.isArray(e.parts) ? e.parts : [];
    for (const p of parts) {
      if (!p.replace_every_days) continue;
      const anchor = p.last_replaced_at || p.installed_at;
      if (!anchor) continue;
      const elapsed = daysSinceCalendarDay(anchor);
      if (elapsed == null) continue;
      const remainingDays = p.replace_every_days - Math.max(0, elapsed);
      const f = runwayBucket(remainingDays);
      const parentName = equipmentName(e, catalog);
      const label = p.label || p.kind || 'Part';
      items.push({
        id: `part:${p.id}`,
        name: `${label} · ${parentName}${e.brand ? ' (' + e.brand + ')' : ''}`,
        kind: 'part',
        remaining: remainingDays,
        daily_use: 1,
        equipment_id: e.id,
        part_id: p.id,
        // part_kind + brand drive the Phase 2 reorder-URL resolver so
        // Today's Plan can build a fallback BRS-search link when the
        // user hasn't pasted an explicit reorder_url on the part.
        part_kind: p.kind || null,
        brand: e.brand || null,
        reorder_url: p.reorder_url || null,
        f,
      });
    }
  }
  return items;
}

// The equipment's own name ("RO/DI unit"), not its stored key ("rodi").
function equipmentName(e, catalog) {
  return catalog?.kinds?.[e.kind]?.label || (e.kind ? e.kind.replace(/_/g, ' ') : 'equipment');
}

// Forecast-bucket helper shared by the runway emitters. Maps a
// remaining-days number to the same status/label shape supplyForecast
// uses, so cartridges, parts, and service all speak one language in
// Today's Plan.
function runwayBucket(remainingDays) {
  if (remainingDays < 0)
    return { status: 'critical', label: 'Past due', daysLow: 0, daysHigh: 0, days: 0 };
  // The due day itself, as Equipment says it (cadenceStatus): Home said
  // "Past due" while the card said "Due today" (review, 2026-10-24).
  if (remainingDays === 0)
    return { status: 'critical', label: 'Due today', daysLow: 0, daysHigh: 0, days: 0 };
  if (remainingDays <= 7)
    return {
      status: 'critical',
      label: `~${remainingDays} d left`,
      daysLow: remainingDays,
      daysHigh: remainingDays,
      days: remainingDays,
    };
  if (remainingDays <= 30)
    return {
      status: 'low',
      label: `~${remainingDays} d`,
      daysLow: remainingDays,
      daysHigh: remainingDays,
      days: remainingDays,
    };
  return {
    status: 'adequate',
    label: `~${remainingDays} d`,
    daysLow: remainingDays,
    daysHigh: remainingDays,
    days: remainingDays,
  };
}

// Phase 5 — recurring SERVICE (maintenance) items from equipment_parts.
// Distinct from equipmentPartItems (which is the reorder/consumable
// runway): these are upkeep tasks (clean Radion fans, vinegar-soak an
// impeller, recalibrate a pH probe) on a service_every_days cadence.
// Surfaced in Today's Plan as a maintenance line, NOT a reorder — they
// consume nothing, so there's a "Done" action, no "Reorder" link.
export function equipmentServiceItems(tank, catalog = null) {
  const items = [];
  for (const e of tank?.equipment || []) {
    const parts = Array.isArray(e.parts) ? e.parts : [];
    for (const p of parts) {
      if (!p.service_every_days) continue;
      // Service anchor: last_serviced_at, else installed_at (a freshly
      // added part is "serviced" as of install).
      const anchor = p.last_serviced_at || p.installed_at;
      if (!anchor) continue;
      const elapsed = daysSinceCalendarDay(anchor);
      if (elapsed == null) continue;
      const remainingDays = p.service_every_days - Math.max(0, elapsed);
      const parentName = equipmentName(e, catalog);
      const action = p.service_label || `Service ${p.label || p.kind || 'part'}`;
      items.push({
        id: `service:${p.id}`,
        name: `${action} · ${parentName}${e.brand ? ' (' + e.brand + ')' : ''}`,
        action,
        kind: 'service',
        remaining: remainingDays,
        equipment_id: e.id,
        part_id: p.id,
        f: runwayBucket(remainingDays),
      });
    }
  }
  return items;
}
